package br.com.htms.inspeccaocampo.auth

import androidx.compose.runtime.NoLiveLiterals
import br.com.htms.inspeccaocampo.platform.R2Photos
import br.com.htms.inspeccaocampo.platform.isSafariOrIosWebClient
import kotlin.coroutines.resume
import kotlinx.browser.localStorage
import kotlinx.coroutines.CancellableContinuation
import kotlinx.coroutines.suspendCancellableCoroutine
import org.khronos.webgl.ArrayBuffer
import org.khronos.webgl.Uint8Array
import org.khronos.webgl.get
import org.w3c.files.Blob
import org.w3c.files.BlobPropertyBag
import org.w3c.xhr.FormData

@NoLiveLiterals
actual object AuthApi {
    private const val TOKEN_KEY = "icd_auth_token"
    private const val EXP_KEY = "icd_auth_exp"
    private const val USER_KEY = "icd_auth_user"

    private var session: AuthSession? = null

    actual fun isLoginRequired(): Boolean = R2Photos.configOrNull() != null

    actual fun currentSession(): AuthSession? = session

    actual suspend fun restoreSession(): AuthSession? {
        val token = localStorage.getItem(TOKEN_KEY) ?: return null
        val exp = localStorage.getItem(EXP_KEY)?.toDoubleOrNull()?.toLong() ?: 0L
        val now = (js("Date.now()") as Number).toLong()
        if (exp > 0 && now > exp) {
            logout()
            return null
        }
        val cachedUser = localStorage.getItem(USER_KEY)
        if (!cachedUser.isNullOrBlank()) {
            parseUserJson(cachedUser)?.let { u ->
                session = AuthSession(token = token, expiresAt = exp, user = u)
            }
        }
        // Cache local: libera o hub imediatamente. /auth/me não deve segurar
        // "Verificando sessão...".
        val cached = session
        if (cached != null) return cached
        // Sem cache de usuário: valida o token (timeout curto no boot).
        // Timeout/rede NÃO apagam o token (só 401/403) — evita kick no cold start.
        val meResult = fetchMeResult(token, timeoutMs = 8_000)
        val me = meResult.getOrNull()
        if (me != null) {
            session = AuthSession(token = token, expiresAt = exp, user = me)
            persist(session!!)
            return session
        }
        val err = meResult.exceptionOrNull()?.message.orEmpty()
        val unauthorized = err.contains("401") ||
            err.contains("403") ||
            err.contains("Não autorizado", ignoreCase = true) ||
            err.contains("nao autorizado", ignoreCase = true) ||
            err.contains("unauthorized", ignoreCase = true)
        if (unauthorized) {
            logout()
        }
        return null
    }

    actual suspend fun refreshSession(): AuthSession? {
        val s = session ?: return null
        val me = fetchMeResult(s.token, timeoutMs = 8_000).getOrNull() ?: return s
        if (me == s.user) return s
        val next = s.copy(user = me)
        session = next
        persist(next)
        return next
    }

    actual suspend fun login(username: String, password: String): Result<AuthSession> {
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload =
                """{"username":${jsonStr(username)},"password":${jsonStr(password)}}"""
            // Login: timeout mais curto que o default de uploads (45s).
            val text = httpJson("POST", "$base/auth/login", payload, null, timeoutMs = 20_000)
            val parsed = JSON.parse<dynamic>(text)
            if (parsed.token == null) {
                val err = (parsed.error as? String) ?: "Falha no login"
                return Result.failure(IllegalStateException(err))
            }
            val userDyn = parsed.user
            val user = AuthUser(
                id = userDyn.id as String,
                username = userDyn.username as String,
                role = userDyn.role as String,
                profileTypes = parseProfileTypesDyn(userDyn),
                acessos = parseAcessosDyn(userDyn),
                createdAt = (userDyn.createdAt as? Number)?.toLong(),
                nome = (userDyn.nome as? String)?.trim()?.takeIf { it.isNotEmpty() }
            )
            val s = AuthSession(
                token = parsed.token as String,
                expiresAt = (parsed.expiresAt as Number).toLong(),
                user = user
            )
            session = s
            persist(s)
            Result.success(s)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual fun logout() {
        val token = session?.token
        val base = apiBase()
        session = null
        runCatching {
            localStorage.removeItem(TOKEN_KEY)
            localStorage.removeItem(EXP_KEY)
            localStorage.removeItem(USER_KEY)
        }
        // Fire-and-forget: registra LOGOUT no Worker antes do token expirar.
        if (!token.isNullOrBlank() && !base.isNullOrBlank()) {
            runCatching {
                val xhr: dynamic = js("new XMLHttpRequest()")
                xhr.open("POST", "$base/auth/logout", true)
                xhr.setRequestHeader("Accept", "application/json")
                xhr.setRequestHeader("Authorization", "Bearer $token")
                applyIcdClientHeaders(xhr)
                xhr.send()
            }
        }
    }

    actual suspend fun listInspections(): Result<List<RemoteInspection>> =
        listInspectionsInternal(consolidado = false, fromMs = null, toMs = null)

    actual suspend fun listInspectionsForConsolidado(
        fromMs: Long?,
        toMs: Long?
    ): Result<List<RemoteInspection>> =
        listInspectionsInternal(consolidado = true, fromMs = fromMs, toMs = toMs)

    actual suspend fun fetchConsolidadoPayloads(ids: List<String>): Result<List<ConsolidadoPayloadRow>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        val clean = ids.map { it.trim() }.filter { it.isNotEmpty() }.distinct().take(100)
        if (clean.isEmpty()) return Result.success(emptyList())
        return try {
            val idsJson = clean.joinToString(",", prefix = "[", postfix = "]") { jsonStr(it) }
            val text = httpJson(
                "POST",
                "$base/inspections/consolidado-payloads",
                """{"ids":$idsJson}""",
                s.token,
                timeoutMs = 120_000
            )
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.items ?: return Result.success(emptyList())
            val out = mutableListOf<ConsolidadoPayloadRow>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                val id = it.id as? String ?: continue
                val json = it.json as? String ?: continue
                if (json.isBlank()) continue
                out.add(
                    ConsolidadoPayloadRow(
                        id = id,
                        username = (it.username as? String).orEmpty(),
                        json = json
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    private suspend fun listInspectionsInternal(
        consolidado: Boolean,
        fromMs: Long?,
        toMs: Long?
    ): Result<List<RemoteInspection>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val qs = buildString {
                if (consolidado) append("mode=consolidado")
                if (fromMs != null && fromMs > 0L) {
                    if (isNotEmpty()) append('&')
                    append("from=").append(fromMs)
                }
                if (toMs != null && toMs > 0L) {
                    if (isNotEmpty()) append('&')
                    append("to=").append(toMs)
                }
            }
            val url = if (qs.isEmpty()) "$base/inspections" else "$base/inspections?$qs"
            val text = httpJson("GET", url, null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.inspections ?: return Result.success(emptyList())
            val out = mutableListOf<RemoteInspection>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                val summary = it.summary ?: js("{}")
                out.add(
                    RemoteInspection(
                        id = it.id as String,
                        localId = (it.localId as? Number)?.toLong(),
                        createdAt = (it.createdAt as Number).toLong(),
                        username = it.username as? String,
                        summaryTst = (summary.tstResponsavel as? String) ?: "",
                        summaryLocal = (summary.localTexto as? String) ?: "",
                        summaryEfetivo = when (val e = summary.efetivo) {
                            is Number -> e.toString()
                            is String -> e
                            else -> null
                        },
                        summaryObra = summary.obra as? String,
                        dataHorarioMs = (summary.dataHorarioMs as? Number)?.toLong(),
                        pdfFileName = summary.pdfFileName as? String,
                        excelFileName = summary.excelFileName as? String,
                        formType = summary.formType as? String,
                        hasPdf = it.hasPdf == true,
                        hasExcel = it.hasExcel == true,
                        hasJson = it.hasJson == true
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun uploadInspection(
        localId: Long,
        createdAt: Long,
        summaryTst: String,
        summaryLocal: String,
        summaryEfetivo: String?,
        summaryObra: String?,
        dataHorarioMs: Long?,
        formType: String?,
        emailTo: String?,
        pdfFileName: String?,
        excelFileName: String?,
        pdfBytes: ByteArray?,
        excelBytes: ByteArray?,
        jsonText: String?
    ): Result<String> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val fd = FormData()
            val pdfName = pdfFileName?.takeIf { it.isNotBlank() } ?: "inspecao.pdf"
            val excelName = excelFileName?.takeIf { it.isNotBlank() } ?: "inspecao.xlsx"
            val metaStr = buildString {
                append("{\"localId\":$localId,\"createdAt\":$createdAt,\"summary\":{")
                append("\"tstResponsavel\":${jsonStr(summaryTst)},")
                append("\"localTexto\":${jsonStr(summaryLocal)},")
                append("\"efetivo\":")
                if (summaryEfetivo == null) append("null") else append(jsonStr(summaryEfetivo))
                append(",\"obra\":")
                if (summaryObra.isNullOrBlank()) append("null") else append(jsonStr(summaryObra))
                append(",\"dataHorarioMs\":")
                if (dataHorarioMs == null) append("null") else append(dataHorarioMs)
                append(",\"formType\":")
                if (formType.isNullOrBlank()) append("null") else append(jsonStr(formType))
                append(",\"emailTo\":")
                if (emailTo.isNullOrBlank()) append("null") else append(jsonStr(emailTo))
                append(",\"pdfFileName\":")
                if (pdfFileName.isNullOrBlank()) append("null") else append(jsonStr(pdfName))
                append(",\"excelFileName\":")
                if (excelFileName.isNullOrBlank()) append("null") else append(jsonStr(excelName))
                append("}}")
            }
            fd.append("meta", metaStr)
            if (pdfBytes != null && pdfBytes.isNotEmpty()) {
                formAppendBlob(fd, "pdf", bytesToBlob(pdfBytes, "application/pdf"), pdfName)
            }
            if (excelBytes != null && excelBytes.isNotEmpty()) {
                formAppendBlob(
                    fd,
                    "excel",
                    bytesToBlob(
                        excelBytes,
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    ),
                    excelName
                )
            }
            if (!jsonText.isNullOrBlank()) {
                formAppendBlob(
                    fd,
                    "json",
                    bytesToBlob(jsonText.encodeToByteArray(), "application/json"),
                    "inspecao.json"
                )
            }
            val text = httpForm("POST", "$base/inspections", fd, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val id = parsed.id as? String
            if (id.isNullOrBlank()) {
                val err = (parsed.error as? String) ?: "Falha no upload"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(id)
            }
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun attachInspectionAttachments(
        remoteId: String,
        pdfBytes: ByteArray?,
        excelBytes: ByteArray?,
        pdfFileName: String?,
        excelFileName: String?,
        jsonText: String?
    ): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        if (remoteId.isBlank()) return Result.failure(IllegalStateException("ID remoto inválido"))
        if ((pdfBytes == null || pdfBytes.isEmpty()) &&
            (excelBytes == null || excelBytes.isEmpty()) &&
            jsonText.isNullOrBlank()
        ) {
            return Result.failure(IllegalStateException("Nenhum anexo para enviar"))
        }
        return try {
            val fd = FormData()
            val pdfName = pdfFileName?.takeIf { it.isNotBlank() } ?: "inspecao.pdf"
            val excelName = excelFileName?.takeIf { it.isNotBlank() } ?: "inspecao.xlsx"
            if (pdfBytes != null && pdfBytes.isNotEmpty()) {
                formAppendBlob(fd, "pdf", bytesToBlob(pdfBytes, "application/pdf"), pdfName)
            }
            if (excelBytes != null && excelBytes.isNotEmpty()) {
                formAppendBlob(
                    fd,
                    "excel",
                    bytesToBlob(
                        excelBytes,
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                    ),
                    excelName
                )
            }
            if (!jsonText.isNullOrBlank()) {
                formAppendBlob(
                    fd,
                    "json",
                    bytesToBlob(jsonText.encodeToByteArray(), "application/json"),
                    "inspecao.json"
                )
            }
            val id = encodeUriComponent(remoteId)
            val text = httpForm("POST", "$base/admin/inspections/$id/repair-attachments", fd, s.token)
            val parsed = JSON.parse<dynamic>(text)
            if (parsed.ok == true) {
                Result.success(Unit)
            } else {
                val err = (parsed.error as? String) ?: "Falha ao anexar PDF/Excel"
                Result.failure(IllegalStateException(err))
            }
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun downloadInspectionBytes(remoteId: String, kind: String): Result<ByteArray> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val bytes = httpBytes("GET", "$base/inspections/$remoteId/$kind", s.token)
            if (bytes.isEmpty()) Result.failure(IllegalStateException("Arquivo vazio"))
            else Result.success(bytes)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteInspection(remoteId: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val id = encodeUriComponent(remoteId)
            // POST evita bloqueio de DELETE em alguns proxies/browsers
            httpJson("POST", "$base/inspections/$id/delete", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun listUsers(): Result<List<AuthUserListItem>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/users", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.users ?: return Result.success(emptyList())
            val out = mutableListOf<AuthUserListItem>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                out.add(
                    AuthUserListItem(
                        id = it.id as String,
                        username = it.username as String,
                        role = it.role as String,
                        profileTypes = parseProfileTypesDyn(it),
                        acessos = parseAcessosDyn(it),
                        createdAt = (it.createdAt as? Number)?.toLong(),
                        nome = (it.nome as? String)?.trim()?.takeIf { n -> n.isNotEmpty() },
                        ativosAbas = it.ativosAbas as? String,
                        ativosCentros = it.ativosCentros as? String
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun listUserDisplayNames(): Result<List<UserDisplayName>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/user-names", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.names ?: return Result.success(emptyList())
            val out = mutableListOf<UserDisplayName>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                val username = it.username as? String ?: continue
                val nome = (it.nome as? String)?.trim()?.takeIf { n -> n.isNotEmpty() }
                val display = (it.display as? String)?.trim()?.takeIf { d -> d.isNotEmpty() }
                    ?: nome
                    ?: username
                out.add(
                    UserDisplayName(
                        username = username,
                        nome = nome,
                        display = display,
                        profileTypes = parseProfileTypesDyn(it),
                        acessos = parseAcessosDyn(it)
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createUser(
        username: String,
        password: String,
        role: String,
        profileTypes: List<String>,
        nome: String?,
        acessos: List<String>?
    ): Result<AuthUserListItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val typesJson = ProfileTypes.normalize(profileTypes).joinToString(",") { jsonStr(it) }
            val nomePart = if (nome.isNullOrBlank()) "" else ""","nome":${jsonStr(nome.trim())}"""
            val acessosPart = acessos?.let { a -> ""","acessos":[""" + a.joinToString(",") { jsonStr(it) } + "]" } ?: ""
            val payload =
                """{"username":${jsonStr(username)},"password":${jsonStr(password)},"role":${jsonStr(role)},"profileTypes":[$typesJson]$nomePart$acessosPart}"""
            val text = httpJson("POST", "$base/users", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val u = parsed.user
            if (u == null) {
                val err = (parsed.error as? String) ?: "Falha ao criar usuário"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(
                    AuthUserListItem(
                        id = u.id as String,
                        username = u.username as String,
                        role = u.role as String,
                        profileTypes = parseProfileTypesDyn(u),
                        acessos = parseAcessosDyn(u),
                        createdAt = (u.createdAt as? Number)?.toLong(),
                        nome = (u.nome as? String)?.trim()?.takeIf { it.isNotEmpty() }
                    )
                )
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun updateUser(
        userId: String,
        role: String?,
        profileTypes: List<String>?,
        password: String?,
        nome: String?,
        acessos: List<String>?,
        ativosAbas: String?,
        ativosCentros: String?
    ): Result<AuthUserListItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val parts = mutableListOf<String>()
            if (role != null) parts.add("\"role\":${jsonStr(role)}")
            if (profileTypes != null) {
                val typesJson = ProfileTypes.normalize(profileTypes).joinToString(",") { jsonStr(it) }
                parts.add("\"profileTypes\":[$typesJson]")
            }
            if (!password.isNullOrBlank()) parts.add("\"password\":${jsonStr(password)}")
            if (nome != null) parts.add("\"nome\":${jsonStr(nome.trim())}")
            if (acessos != null) parts.add("\"acessos\":[" + acessos.joinToString(",") { jsonStr(it) } + "]")
            if (ativosAbas != null) parts.add("\"ativosAbas\":${jsonStr(ativosAbas)}")
            if (ativosCentros != null) parts.add("\"ativosCentros\":${jsonStr(ativosCentros)}")
            val payload = "{${parts.joinToString(",")}}"
            val id = encodeUriComponent(userId)
            val text = httpJson("PATCH", "$base/users/$id", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val u = parsed.user
            if (u == null) {
                val err = (parsed.error as? String) ?: "Falha ao atualizar usuário"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(
                    AuthUserListItem(
                        id = u.id as String,
                        username = u.username as String,
                        role = u.role as String,
                        profileTypes = parseProfileTypesDyn(u),
                        acessos = parseAcessosDyn(u),
                        createdAt = (u.createdAt as? Number)?.toLong(),
                        nome = (u.nome as? String)?.trim()?.takeIf { it.isNotEmpty() },
                        ativosAbas = u.ativosAbas as? String,
                        ativosCentros = u.ativosCentros as? String
                    )
                )
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteUser(userId: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val id = encodeUriComponent(userId)
            // POST evita bloqueio de DELETE em alguns proxies/browsers (TypeError: Failed to fetch)
            httpJson("POST", "$base/users/$id/delete", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun listAccessLogs(
        limit: Int,
        action: String?,
        user: String?,
        q: String?,
        fromMs: Long?,
        toMs: Long?
    ): Result<List<AccessLogEntry>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val lim = limit.coerceIn(1, 1000)
            val qs = buildString {
                append("limit=$lim")
                if (!action.isNullOrBlank()) append("&action=${encodeUriComponent(action.trim())}")
                if (!user.isNullOrBlank()) append("&user=${encodeUriComponent(user.trim())}")
                if (!q.isNullOrBlank()) append("&q=${encodeUriComponent(q.trim())}")
                if (fromMs != null && fromMs > 0L) append("&from=$fromMs")
                if (toMs != null && toMs > 0L) append("&to=$toMs")
            }
            val text = httpJson("GET", "$base/access-logs?$qs", null, s.token, timeoutMs = 25_000)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.logs ?: return Result.success(emptyList())
            val out = mutableListOf<AccessLogEntry>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                out.add(
                    AccessLogEntry(
                        id = it.id as String,
                        createdAt = (it.createdAt as? Number)?.toLong() ?: 0L,
                        actorUserId = it.actorUserId as? String,
                        actorUsername = it.actorUsername as? String,
                        action = it.action as String,
                        target = it.target as? String,
                        detail = it.detail as? String,
                        ip = it.ip as? String
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun listTrash(): Result<List<TrashItem>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/trash", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.items ?: return Result.success(emptyList())
            val out = mutableListOf<TrashItem>()
            val len = arr.length as Int
            for (i in 0 until len) {
                val it = arr[i]
                out.add(
                    TrashItem(
                        type = it.type as String,
                        id = it.id as String,
                        label = (it.label as? String) ?: it.id as String,
                        subtitle = it.subtitle as? String,
                        username = it.username as? String,
                        obra = it.obra as? String,
                        ano = (it.ano as? Number)?.toInt(),
                        mes = (it.mes as? Number)?.toInt(),
                        deletedAt = (it.deletedAt as? Number)?.toLong() ?: 0L,
                        purgeAt = (it.purgeAt as? Number)?.toLong(),
                        deletedByUsername = it.deletedByUsername as? String,
                        formType = it.formType as? String
                    )
                )
            }
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun restoreTrash(type: String, id: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload =
                """{"type":${jsonStr(type.trim())},"id":${jsonStr(id.trim())}}"""
            httpJson("POST", "$base/trash/restore", payload, s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun purgeTrash(type: String, id: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload =
                """{"type":${jsonStr(type.trim())},"id":${jsonStr(id.trim())}}"""
            httpJson("POST", "$base/trash/purge", payload, s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun logAccessEvent(
        action: String,
        target: String?,
        detail: String?
    ): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        val act = action.trim()
        if (act.isEmpty()) return Result.failure(IllegalArgumentException("action vazia"))
        return try {
            val payload = buildString {
                append("""{"action":${jsonStr(act)}""")
                if (!target.isNullOrBlank()) {
                    append(""","target":${jsonStr(target)}""")
                }
                if (!detail.isNullOrBlank()) {
                    append(""","detail":${jsonStr(detail)}""")
                }
                append("}")
            }
            httpJson("POST", "$base/access-logs", payload, s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun listObrasAtivas(): Result<List<br.com.htms.inspeccaocampo.data.ObraItem>> {
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        val token = session?.token
            ?: return Result.failure(IllegalStateException("Não autenticado"))
        return try {
            val text = httpJson("GET", "$base/obras", null, token)
            Result.success(parseObras(text))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun listObrasAdmin(): Result<List<br.com.htms.inspeccaocampo.data.ObraItem>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/obras/manage", null, s.token)
            Result.success(parseObras(text))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createObra(nome: String): Result<br.com.htms.inspeccaocampo.data.ObraItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson(
                "POST",
                "$base/obras",
                """{"nome":${jsonStr(nome)}}""",
                s.token
            )
            val parsed = JSON.parse<dynamic>(text)
            val o = parsed.obra
            if (o == null) {
                val err = (parsed.error as? String) ?: "Falha ao criar obra"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(obraFromDyn(o))
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun updateObra(
        id: String,
        nome: String?,
        ativo: Boolean?
    ): Result<br.com.htms.inspeccaocampo.data.ObraItem> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload = buildString {
                append('{')
                var first = true
                if (nome != null) {
                    append("\"nome\":${jsonStr(nome)}")
                    first = false
                }
                if (ativo != null) {
                    if (!first) append(',')
                    append("\"ativo\":$ativo")
                }
                append('}')
            }
            val text = httpJson("PATCH", "$base/obras/$id", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val o = parsed.obra
            if (o == null) {
                val err = (parsed.error as? String) ?: "Falha ao atualizar obra"
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(obraFromDyn(o))
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun fetchAppConfigJson(): Result<String?> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            // Timeout curto: boot usa config local e não deve esperar 45s.
            val text = httpJson("GET", "$base/app-config", null, s.token, timeoutMs = 12_000)
            val parsed = JSON.parse<dynamic>(text)
            val cfg = parsed.config
            if (cfg == null || cfg == undefined) {
                Result.success(null)
            } else {
                Result.success(JSON.stringify(cfg))
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun pushAppConfigJson(configJson: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson(
                "PUT",
                "$base/app-config",
                """{"config":$configJson}""",
                s.token
            )
            val parsed = JSON.parse<dynamic>(text)
            val err = parsed.error as? String
            if (!err.isNullOrBlank()) {
                Result.failure(IllegalStateException(err))
            } else {
                Result.success(Unit)
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun listRemDossiers(all: Boolean): Result<List<br.com.htms.inspeccaocampo.data.RemDossier>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val q = if (all) "?all=1" else ""
            val text = httpJson("GET", "$base/rem/dossiers$q", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.dossiers ?: return Result.success(emptyList())
            val out = mutableListOf<br.com.htms.inspeccaocampo.data.RemDossier>()
            val len = arr.length as Int
            for (i in 0 until len) out.add(remDossierFromDyn(arr[i], emptyList()))
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createRemDossier(obra: String, ano: Int): Result<br.com.htms.inspeccaocampo.data.RemDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload = """{"obra":${jsonStr(obra)},"ano":$ano}"""
            val text = httpJson("POST", "$base/rem/dossiers", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha"))
            Result.success(remDossierFromDyn(d, emptyList()))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun getRemDossier(id: String): Result<br.com.htms.inspeccaocampo.data.RemDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/rem/dossiers/${encodeUriComponent(id)}", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Não encontrado"))
            val months = mutableListOf<br.com.htms.inspeccaocampo.data.RemMonthRow>()
            val arr = parsed.months
            if (arr != null) {
                val len = arr.length as Int
                for (i in 0 until len) months.add(remMonthFromDyn(arr[i]))
            }
            Result.success(remDossierFromDyn(d, months))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun upsertRemMonth(
        dossierId: String,
        abrangencia: String,
        mes: Int,
        payloadJson: String,
        emailTo: String?
    ): Result<br.com.htms.inspeccaocampo.data.RemMonthRow> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val emailPart = if (emailTo.isNullOrBlank()) "" else ""","emailTo":${jsonStr(emailTo)}"""
            val body = """{"abrangencia":${jsonStr(abrangencia)},"mes":$mes,"payload":$payloadJson$emailPart}"""
            val text = httpJson("PUT", "$base/rem/dossiers/${encodeUriComponent(dossierId)}/months", body, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val m = parsed.month ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha"))
            Result.success(remMonthFromDyn(m))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteRemDossier(id: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            httpJson("DELETE", "$base/rem/dossiers/${encodeUriComponent(id)}", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun downloadRemExcel(dossierId: String): Result<ByteArray> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val bytes = httpBytes("GET", "$base/rem/dossiers/${encodeUriComponent(dossierId)}/excel", s.token)
            Result.success(bytes)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun resetRemAdmin(
        scope: String,
        obra: String?,
        ano: Int?,
        mes: Int?
    ): Result<String> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val parts = mutableListOf(""" "scope":${jsonStr(scope)} """)
            if (!obra.isNullOrBlank()) parts += """ "obra":${jsonStr(obra)} """
            if (ano != null) parts += """ "ano":$ano """
            if (mes != null) parts += """ "mes":$mes """
            val body = "{" + parts.joinToString(",") { it.trim() } + "}"
            val text = httpJson("POST", "$base/rem/admin/reset", body, s.token)
            val parsed = JSON.parse<dynamic>(text)
            if (parsed.ok == true) {
                val deletedM = (parsed.deletedMonths as? Number)?.toInt() ?: 0
                val deletedD = (parsed.deletedDossiers as? Number)?.toInt() ?: 0
                Result.success(
                    when (scope) {
                        "geral" -> "REM geral zerado ($deletedD dossiês, $deletedM meses)."
                        "obra_ano" -> "REM obra/ano zerado ($deletedD dossiês, $deletedM meses)."
                        else -> "REM mês zerado ($deletedM linhas)."
                    }
                )
            } else {
                Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha ao zerar REM"))
            }
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun fetchRemMetasJson(): Result<String> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            Result.success(httpJson("GET", "$base/rem/metas", null, s.token))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun saveRemMetasJson(body: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            httpJson("PUT", "$base/rem/metas", body, s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }


    actual suspend fun listAcompAmbDossiers(all: Boolean): Result<List<br.com.htms.inspeccaocampo.data.AcompAmbDossier>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val q = if (all) "?all=1" else ""
            val text = httpJson("GET", "$base/acomp-amb/dossiers$q", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.dossiers ?: return Result.success(emptyList())
            val out = mutableListOf<br.com.htms.inspeccaocampo.data.AcompAmbDossier>()
            val len = arr.length as Int
            for (i in 0 until len) out.add(acompAmbDossierFromDyn(arr[i], emptyList()))
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createAcompAmbDossier(obra: String, ano: Int): Result<br.com.htms.inspeccaocampo.data.AcompAmbDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload = """{"obra":${jsonStr(obra)},"ano":$ano}"""
            val text = httpJson("POST", "$base/acomp-amb/dossiers", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha"))
            Result.success(acompAmbDossierFromDyn(d, emptyList()))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun getAcompAmbDossier(id: String): Result<br.com.htms.inspeccaocampo.data.AcompAmbDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/acomp-amb/dossiers/${encodeUriComponent(id)}", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Não encontrado"))
            val months = mutableListOf<br.com.htms.inspeccaocampo.data.AcompAmbMonthRow>()
            val arr = parsed.months
            if (arr != null) {
                val len = arr.length as Int
                for (i in 0 until len) months.add(acompAmbMonthFromDyn(arr[i]))
            }
            Result.success(acompAmbDossierFromDyn(d, months))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun upsertAcompAmbMonth(
        dossierId: String,
        aba: String,
        mes: Int,
        payloadJson: String,
        emailTo: String?
    ): Result<br.com.htms.inspeccaocampo.data.AcompAmbMonthRow> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val emailPart = if (emailTo.isNullOrBlank()) "" else ""","emailTo":${jsonStr(emailTo)}"""
            val body = """{"aba":${jsonStr(aba)},"mes":$mes,"payload":$payloadJson$emailPart}"""
            val text = httpJson("PUT", "$base/acomp-amb/dossiers/${encodeUriComponent(dossierId)}/months", body, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val m = parsed.month ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha"))
            Result.success(acompAmbMonthFromDyn(m))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteAcompAmbDossier(id: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            httpJson("DELETE", "$base/acomp-amb/dossiers/${encodeUriComponent(id)}", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun downloadAcompAmbExcel(dossierId: String): Result<ByteArray> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            Result.success(httpBytes("GET", "$base/acomp-amb/dossiers/${encodeUriComponent(dossierId)}/excel", s.token))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }



    actual suspend fun uploadAcompAmbPhoto(
        dossierId: String,
        bytes: ByteArray,
        slot: String,
        index: Int
    ): Result<br.com.htms.inspeccaocampo.auth.RelSegPhotoUpload> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        if (bytes.isEmpty()) return Result.failure(IllegalStateException("Foto vazia"))
        val q = "?slot=${encodeUriComponent(slot)}&index=$index"
        val contentType = if (bytes.size > 3 && bytes[0] == 0x89.toByte()) "image/png" else "image/jpeg"
        return suspendCancellableCoroutine { cont ->
            try {
                val u8 = Uint8Array(bytes.size)
                for (i in bytes.indices) u8.asDynamic()[i] = bytes[i]
                val blob = Blob(arrayOf(u8), BlobPropertyBag(type = contentType))
                val opts = js("({})")
                opts.method = "POST"
                opts.headers = js("({})")
                opts.headers["Content-Type"] = contentType
                opts.headers["Authorization"] = "Bearer ${s.token}"
                opts.body = blob
                val promise: dynamic = js("fetch")(
                    "$base/acomp-amb/dossiers/${encodeUriComponent(dossierId)}/photos$q",
                    opts
                )
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        response.text().then(
                            { text ->
                                if (!ok) {
                                    val err = runCatching {
                                        val p = JSON.parse<dynamic>(text as String)
                                        (p.error as? String) ?: "HTTP ${response.status}"
                                    }.getOrElse { "HTTP ${response.status}" }
                                    if (cont.isActive) cont.resume(Result.failure(IllegalStateException(err)))
                                } else {
                                    runCatching {
                                        val parsed = JSON.parse<dynamic>(text as String)
                                        val key = parsed.key as String
                                        val url = (parsed.url as? String) ?: ""
                                        RelSegPhotoUpload(key = key, url = url)
                                    }.fold(
                                        onSuccess = { up -> if (cont.isActive) cont.resume(Result.success(up)) },
                                        onFailure = { e -> if (cont.isActive) cont.resume(Result.failure(e)) }
                                    )
                                }
                            },
                            { err ->
                                if (cont.isActive) cont.resume(Result.failure(Exception("$err")))
                            }
                        )
                        null
                    },
                    { err ->
                        if (cont.isActive) cont.resume(Result.failure(Exception("$err")))
                        null
                    }
                )
            } catch (t: Throwable) {
                if (cont.isActive) cont.resume(Result.failure(t))
            }
        }
    }

    actual suspend fun listRelSegDossiers(all: Boolean): Result<List<br.com.htms.inspeccaocampo.data.RelSegDossier>> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val q = if (all) "?all=1" else ""
            val text = httpJson("GET", "$base/rel-seg/dossiers$q", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val arr = parsed.dossiers ?: return Result.success(emptyList())
            val out = mutableListOf<br.com.htms.inspeccaocampo.data.RelSegDossier>()
            val len = arr.length as Int
            for (i in 0 until len) out.add(relSegDossierFromDyn(arr[i]))
            Result.success(out)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun createRelSegDossier(obra: String, ano: Int, mes: Int): Result<br.com.htms.inspeccaocampo.data.RelSegDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val payload = """{"obra":${jsonStr(obra)},"ano":$ano,"mes":$mes}"""
            val text = httpJson("POST", "$base/rel-seg/dossiers", payload, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha"))
            Result.success(relSegDossierFromDyn(d))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun getRelSegDossier(id: String): Result<br.com.htms.inspeccaocampo.data.RelSegDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/rel-seg/dossiers/${encodeUriComponent(id)}", null, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Não encontrado"))
            Result.success(relSegDossierFromDyn(d))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun saveRelSegDossier(
        id: String,
        payloadJson: String,
        emailTo: String?
    ): Result<br.com.htms.inspeccaocampo.data.RelSegDossier> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val emailPart = if (emailTo.isNullOrBlank()) "" else ""","emailTo":${jsonStr(emailTo)}"""
            val body = """{"payload":$payloadJson$emailPart}"""
            val text = httpJson("PUT", "$base/rel-seg/dossiers/${encodeUriComponent(id)}", body, s.token)
            val parsed = JSON.parse<dynamic>(text)
            val d = parsed.dossier ?: return Result.failure(IllegalStateException((parsed.error as? String) ?: "Falha"))
            Result.success(relSegDossierFromDyn(d))
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun deleteRelSegDossier(id: String): Result<Unit> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            httpJson("DELETE", "$base/rel-seg/dossiers/${encodeUriComponent(id)}", "{}", s.token)
            Result.success(Unit)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun downloadRelSegPptx(dossierId: String): Result<ByteArray> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val bytes = httpBytes("GET", "$base/rel-seg/dossiers/${encodeUriComponent(dossierId)}/pptx", s.token)
            Result.success(bytes)
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    actual suspend fun uploadRelSegPhoto(
        dossierId: String,
        bytes: ByteArray,
        kind: String,
        index: Int?,
        block: Int?,
        photo: Int?
    ): Result<RelSegPhotoUpload> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        if (bytes.isEmpty()) return Result.failure(IllegalStateException("Foto vazia"))
        val q = buildString {
            append("?kind=${encodeUriComponent(kind)}")
            if (index != null) append("&index=$index")
            if (block != null) append("&block=$block")
            if (photo != null) append("&photo=$photo")
        }
        val contentType = if (bytes.size > 3 && bytes[0] == 0x89.toByte()) "image/png" else "image/jpeg"
        return suspendCancellableCoroutine { cont ->
            try {
                val u8 = Uint8Array(bytes.size)
                for (i in bytes.indices) u8.asDynamic()[i] = bytes[i]
                val blob = Blob(arrayOf(u8), BlobPropertyBag(type = contentType))
                val opts = js("({})")
                opts.method = "POST"
                opts.headers = js("({})")
                opts.headers["Content-Type"] = contentType
                opts.headers["Authorization"] = "Bearer ${s.token}"
                opts.body = blob
                val promise: dynamic = js("fetch")(
                    "$base/rel-seg/dossiers/${encodeUriComponent(dossierId)}/photos$q",
                    opts
                )
                promise.then(
                    { response ->
                        val ok = (response.ok as? Boolean) == true
                        response.text().then(
                            { text ->
                                if (!ok) {
                                    val err = runCatching {
                                        val p = JSON.parse<dynamic>(text as String)
                                        (p.error as? String) ?: "HTTP ${response.status}"
                                    }.getOrElse { "HTTP ${response.status}" }
                                    if (cont.isActive) cont.resume(Result.failure(IllegalStateException(err)))
                                } else {
                                    runCatching {
                                        val parsed = JSON.parse<dynamic>(text as String)
                                        val key = parsed.key as String
                                        val url = parsed.url as String
                                        RelSegPhotoUpload(key = key, url = url)
                                    }.fold(
                                        onSuccess = { up -> if (cont.isActive) cont.resume(Result.success(up)) },
                                        onFailure = { e -> if (cont.isActive) cont.resume(Result.failure(e)) }
                                    )
                                }
                            },
                            { err ->
                                if (cont.isActive) cont.resume(Result.failure(throwableFromJsErr(err)))
                            }
                        )
                        null
                    },
                    { err ->
                        if (cont.isActive) cont.resume(Result.failure(throwableFromJsErr(err)))
                        null
                    }
                )
            } catch (t: Throwable) {
                if (cont.isActive) cont.resume(Result.failure(friendlyNetworkError(t)))
            }
        }
    }

    actual suspend fun analyzeNrVisual(
        cenaTipo: String,
        descricaoCena: String,
        fotoCount: Int,
        fotosBase64: List<String>
    ): Result<br.com.htms.inspeccaocampo.data.nrvisual.NrVisualAnalyzeResult> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val fotosJson = fotosBase64.take(3).joinToString(",") { jsonStr(it) }
            val payload =
                """{"cenaTipo":${jsonStr(cenaTipo)},"descricaoCena":${jsonStr(descricaoCena)},"fotoCount":$fotoCount,"fotosBase64":[$fotosJson]}"""
            val text = httpJson("POST", "$base/nr-visual/analyze", payload, s.token)
            Result.success(
                br.com.htms.inspeccaocampo.data.nrvisual.NrVisualJson.analyzeResultFromResponse(text)
            )
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun apiJson(method: String, path: String, body: String?): Result<String> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            Result.success(httpJson(method, "$base$path", body, s.token))
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    actual suspend fun apiUploadBytes(path: String, bytes: ByteArray, contentType: String): Result<String> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        if (bytes.isEmpty()) return Result.failure(IllegalStateException("Arquivo vazio"))
        return try {
            Result.success(postBytesXhr("$base$path", bytes, contentType, s.token))
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    private suspend fun postBytesXhr(url: String, bytes: ByteArray, contentType: String, token: String): String =
        suspendCancellableCoroutine { cont ->
            try {
                val u8 = Uint8Array(bytes.size)
                for (i in bytes.indices) u8.asDynamic()[i] = bytes[i]
                val blob = Blob(arrayOf(u8), BlobPropertyBag(type = contentType))
                val xhr: dynamic = js("new XMLHttpRequest()")
                xhr.open("POST", url, true)
                xhr.timeout = 180_000
                xhr.setRequestHeader("Accept", "application/json")
                xhr.setRequestHeader("Content-Type", contentType)
                xhr.setRequestHeader("Authorization", "Bearer $token")
                applyIcdClientHeaders(xhr)
                xhr.onload = {
                    val status = (xhr.status as? Number)?.toInt() ?: 0
                    val text = (xhr.responseText as? String) ?: ""
                    if (status in 200..299) {
                        resumeForm(cont, Result.success(text))
                    } else {
                        val err = runCatching { JSON.parse<dynamic>(text).error as? String }.getOrNull()
                        resumeForm(cont, Result.failure(IllegalStateException(err ?: "HTTP $status")))
                    }
                }
                xhr.onerror = { resumeForm(cont, Result.failure(Exception("Failed to fetch"))) }
                xhr.ontimeout = { resumeForm(cont, Result.failure(Exception("timeout"))) }
                xhr.send(blob)
            } catch (t: Throwable) {
                resumeForm(cont, Result.failure(friendlyNetworkError(t)))
            }
        }

    actual suspend fun apiDownloadBytes(path: String): Result<ByteArray> {
        val s = session ?: return Result.failure(IllegalStateException("Não autenticado"))
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            Result.success(httpBytes("GET", "$base$path", s.token))
        } catch (t: Throwable) {
            Result.failure(friendlyNetworkError(t))
        }
    }

    private fun relSegDossierFromDyn(d: dynamic): br.com.htms.inspeccaocampo.data.RelSegDossier {
        val payloadRaw = JSON.stringify(d.payload ?: js("{}")) as String
        val payload = br.com.htms.inspeccaocampo.data.RelSegJson.payloadFromJson(payloadRaw)
        return br.com.htms.inspeccaocampo.data.RelSegDossier(
            id = d.id as String,
            userId = d.userId as? String,
            username = d.username as? String,
            obra = d.obra as String,
            ano = (d.ano as Number).toInt(),
            mes = (d.mes as Number).toInt(),
            payload = payload,
            createdAt = (d.createdAt as? Number)?.toLong(),
            updatedAt = (d.updatedAt as? Number)?.toLong()
        )
    }
    private fun remDossierFromDyn(
        d: dynamic,
        months: List<br.com.htms.inspeccaocampo.data.RemMonthRow>
    ): br.com.htms.inspeccaocampo.data.RemDossier =
        br.com.htms.inspeccaocampo.data.RemDossier(
            id = d.id as String,
            userId = d.userId as? String,
            username = d.username as? String,
            obra = d.obra as String,
            ano = (d.ano as Number).toInt(),
            createdAt = (d.createdAt as? Number)?.toLong(),
            updatedAt = (d.updatedAt as? Number)?.toLong(),
            months = months
        )

    private fun remMonthFromDyn(m: dynamic): br.com.htms.inspeccaocampo.data.RemMonthRow {
        val payloadRaw = JSON.stringify(m.payload ?: js("{}"))
        val payload = br.com.htms.inspeccaocampo.data.RemJson.payloadFromJson(payloadRaw as String)
        return br.com.htms.inspeccaocampo.data.RemMonthRow(
            id = m.id as? String,
            abrangencia = br.com.htms.inspeccaocampo.data.RemAbrangencia.fromId(m.abrangencia as? String),
            mes = (m.mes as Number).toInt(),
            payload = payload,
            updatedAt = (m.updatedAt as? Number)?.toLong()
        )
    }

    private fun acompAmbDossierFromDyn(
        d: dynamic,
        months: List<br.com.htms.inspeccaocampo.data.AcompAmbMonthRow>
    ): br.com.htms.inspeccaocampo.data.AcompAmbDossier =
        br.com.htms.inspeccaocampo.data.AcompAmbDossier(
            id = d.id as String,
            userId = d.userId as? String,
            username = d.username as? String,
            obra = d.obra as String,
            ano = (d.ano as Number).toInt(),
            createdAt = (d.createdAt as? Number)?.toLong(),
            updatedAt = (d.updatedAt as? Number)?.toLong(),
            months = months
        )

    private fun acompAmbMonthFromDyn(m: dynamic): br.com.htms.inspeccaocampo.data.AcompAmbMonthRow {
        val payloadRaw = JSON.stringify(m.payload ?: js("{}"))
        val payload = br.com.htms.inspeccaocampo.data.AcompAmbJson.payloadFromJson(payloadRaw as String)
        return br.com.htms.inspeccaocampo.data.AcompAmbMonthRow(
            id = m.id as? String,
            aba = br.com.htms.inspeccaocampo.data.AcompAmbAba.fromId(m.aba as? String),
            mes = (m.mes as Number).toInt(),
            payload = payload,
            updatedAt = (m.updatedAt as? Number)?.toLong()
        )
    }


    private fun parseObras(text: String): List<br.com.htms.inspeccaocampo.data.ObraItem> {
        val parsed = JSON.parse<dynamic>(text)
        val arr = parsed.obras ?: return emptyList()
        val out = mutableListOf<br.com.htms.inspeccaocampo.data.ObraItem>()
        val len = arr.length as Int
        for (i in 0 until len) {
            out.add(obraFromDyn(arr[i]))
        }
        return out
    }

    private fun obraFromDyn(o: dynamic): br.com.htms.inspeccaocampo.data.ObraItem =
        br.com.htms.inspeccaocampo.data.ObraItem(
            id = o.id as String,
            nome = o.nome as String,
            ativo = o.ativo != false,
            sortOrder = (o.sortOrder as? Number)?.toInt() ?: 0
        )

    private fun apiBase(): String? = R2Photos.configOrNull()?.baseUrl

    private fun encodeUriComponent(value: String): String =
        js("encodeURIComponent")(value) as String

    /** Mensagem legível quando o browser aborta por rede/CORS (não chega JSON do Worker). */
    private fun friendlyNetworkError(t: Throwable): Throwable {
        val raw = (t.message ?: t.toString()).trim()
        val lower = raw.lowercase()
        if (
            lower.contains("failed to fetch") ||
            lower.contains("networkerror") ||
            lower.contains("network request failed") ||
            lower.contains("load failed") ||
            lower.contains("the network connection was lost") ||
            lower.contains("connection was lost") ||
            lower.contains("cancelled") ||
            lower.contains("canceled") ||
            lower.contains("aborterror") ||
            lower.contains("abort") ||
            lower.contains("the internet connection appears to be offline") ||
            lower.contains("appears to be offline") ||
            lower.contains("cannot parse response") ||
            lower.contains("typeerror") ||
            lower.contains("offline") ||
            lower == "rede"
        ) {
            return IllegalStateException(
                "Falha de rede ou CORS ao contatar a API. Verifique a conexão e tente de novo."
            )
        }
        return t
    }

    private fun throwableFromJsErr(err: dynamic): Throwable {
        val raw = try {
            val name = (err?.name as? String)?.takeIf { it.isNotBlank() } ?: ""
            val msg = (err?.message as? String)?.takeIf { it.isNotBlank() } ?: ""
            val combined = listOf(name, msg).filter { it.isNotEmpty() }.joinToString(" ")
            combined.ifBlank {
                err?.toString()?.takeIf { it.isNotBlank() && it != "[object Object]" } ?: "rede"
            }
        } catch (_: Throwable) {
            "rede"
        }
        return friendlyNetworkError(Exception(raw))
    }

    /** Tamanho de array vindo do JSON.parse. Cast seguro falha em número do JS e zerava a lista. */
    private fun dynLen(raw: dynamic): Int = try {
        raw.length as Int
    } catch (_: Throwable) {
        0
    }

    private fun persist(s: AuthSession) {
        localStorage.setItem(TOKEN_KEY, s.token)
        localStorage.setItem(EXP_KEY, s.expiresAt.toString())
        val typesJson = ProfileTypes.normalize(s.user.profileTypes).joinToString(",") { jsonStr(it) }
        val acessosJson = s.user.acessos?.let { a -> "[" + a.joinToString(",") { jsonStr(it) } + "]" } ?: "null"
        localStorage.setItem(
            USER_KEY,
            """{"id":${jsonStr(s.user.id)},"username":${jsonStr(s.user.username)},"role":${jsonStr(s.user.role)},"profileTypes":[$typesJson],"acessos":$acessosJson,"nome":${
                s.user.nome?.let { jsonStr(it) } ?: "null"
            }}"""
        )
    }

    private fun parseProfileTypesDyn(u: dynamic): List<String> {
        val raw = u.profileTypes ?: return listOf(ProfileTypes.TST)
        val asString = raw as? String
        if (asString != null) {
            return ProfileTypes.fromEncoded(asString).ifEmpty { listOf(ProfileTypes.TST) }
        }
        val collected = mutableListOf<String>()
        val len = dynLen(raw)
        for (i in 0 until len) {
            val v = raw[i] as? String
            if (v != null) collected.add(v)
        }
        return ProfileTypes.normalize(collected).ifEmpty { listOf(ProfileTypes.TST) }
    }

    private fun parseAcessosDyn(u: dynamic): List<String>? {
        val raw = u.acessos ?: return null
        val asString = raw as? String
        if (asString != null) return Acessos.normalizar(asString.split(','))
        val collected = mutableListOf<String>()
        val len = dynLen(raw)
        for (i in 0 until len) {
            val v = raw[i] as? String
            if (v != null) collected.add(v)
        }
        return Acessos.normalizar(collected)
    }

    private fun parseUserJson(raw: String): AuthUser? = try {
        val u = JSON.parse<dynamic>(raw)
        AuthUser(
            id = u.id as String,
            username = u.username as String,
            role = u.role as String,
            profileTypes = parseProfileTypesDyn(u),
            acessos = parseAcessosDyn(u),
            nome = (u.nome as? String)?.trim()?.takeIf { it.isNotEmpty() }
        )
    } catch (_: Throwable) {
        null
    }

    private suspend fun fetchMe(token: String, timeoutMs: Int = 45_000): AuthUser? =
        fetchMeResult(token, timeoutMs).getOrNull()

    private suspend fun fetchMeResult(token: String, timeoutMs: Int = 45_000): Result<AuthUser> {
        val base = apiBase() ?: return Result.failure(IllegalStateException("API não configurada"))
        return try {
            val text = httpJson("GET", "$base/auth/me", null, token, timeoutMs = timeoutMs)
            val parsed = JSON.parse<dynamic>(text)
            val u = parsed.user ?: return Result.failure(IllegalStateException("Sem usuário"))
            Result.success(
                AuthUser(
                    id = u.id as String,
                    username = u.username as String,
                    role = u.role as String,
                    profileTypes = parseProfileTypesDyn(u),
                    acessos = parseAcessosDyn(u),
                    createdAt = (u.createdAt as? Number)?.toLong(),
                    nome = (u.nome as? String)?.trim()?.takeIf { it.isNotEmpty() }
                )
            )
        } catch (t: Throwable) {
            Result.failure(t)
        }
    }

    private fun jsonStr(s: String): String =
        "\"" + s.replace("\\", "\\\\").replace("\"", "\\\"")
            .replace("\n", "\\n").replace("\r", "\\r") + "\""

    private fun bytesToBlob(bytes: ByteArray, mime: String): Blob {
        val u8 = Uint8Array(bytes.size)
        u8.set(Array(bytes.size) { bytes[it] })
        return Blob(arrayOf(u8), BlobPropertyBag(type = mime))
    }

    private fun formAppendBlob(fd: FormData, name: String, blob: Blob, filename: String) {
        // Kotlin/JS FormData tipagem nem sempre expõe filename.
        // Não usar asDynamic() — quebra no runtime Web (TypeError).
        val appendFn: dynamic = js("(function(fd, name, blob, filename){ fd.append(name, blob, filename); })")
        appendFn(fd, name, blob, filename)
    }

    /**
     * JSON via XHR (mais estável no Compose Web/Safari do que fetch+Promise
     * dentro de suspendCancellableCoroutine — o Promise às vezes não resume).
     */
    private suspend fun httpJson(
        method: String,
        url: String,
        body: String?,
        token: String?,
        timeoutMs: Int = 45_000
    ): String = suspendCancellableCoroutine { cont ->
        try {
            val xhr: dynamic = js("new XMLHttpRequest()")
            xhr.open(method, url, true)
            xhr.timeout = timeoutMs
            xhr.setRequestHeader("Accept", "application/json")
            if (token != null) xhr.setRequestHeader("Authorization", "Bearer $token")
            if (body != null) xhr.setRequestHeader("Content-Type", "application/json")
            applyIcdClientHeaders(xhr)
            xhr.onload = {
                val status = (xhr.status as? Number)?.toInt() ?: 0
                val text = (xhr.responseText as? String) ?: ""
                if (status >= 400) {
                    val errMsg = try {
                        val p = JSON.parse<dynamic>(text)
                        (p.error as? String) ?: text
                    } catch (_: Throwable) {
                        text.ifBlank { "HTTP $status" }
                    }
                    resumeForm(cont, Result.failure(IllegalStateException(errMsg)))
                } else if (status == 0) {
                    resumeForm(cont, Result.failure(Exception("Failed to fetch")))
                } else {
                    resumeForm(cont, Result.success(text))
                }
            }
            xhr.onerror = {
                resumeForm(cont, Result.failure(Exception("Failed to fetch")))
            }
            xhr.ontimeout = {
                resumeForm(cont, Result.failure(Exception("timeout")))
            }
            xhr.onabort = {
                resumeForm(cont, Result.failure(Exception("abort")))
            }
            if (body != null) xhr.send(body) else xhr.send()
        } catch (t: Throwable) {
            resumeForm(cont, Result.failure(friendlyNetworkError(t)))
        }
    }

    private fun resumeForm(cont: CancellableContinuation<String>, result: Result<String>) {
        try {
            if (cont.isActive) cont.resumeWith(result)
        } catch (_: Throwable) {
        }
    }

    private fun icdClientVersion(): String =
        runCatching {
            (js(
                "(typeof ICD_ASSET_V !== 'undefined' && ICD_ASSET_V) || " +
                    "(typeof window !== 'undefined' && window.__ICD_ASSET_V) || ''"
            ) as? String).orEmpty()
        }.getOrDefault("")

    private fun applyIcdClientHeaders(xhr: dynamic) {
        xhr.setRequestHeader("X-ICD-Platform", "web")
        val ver = icdClientVersion()
        if (ver.isNotBlank()) xhr.setRequestHeader("X-ICD-Version", ver)
    }

    /**
     * Safari/iOS: XMLHttpRequest é mais estável para multipart grande que fetch.
     * Não abortar no cancel da coroutine — o POST precisa terminar mesmo se o
     * Compose der timeout (AppController recupera via remotoPorLocalId).
     * Nunca usar keepalive: Safari limita ~64KB e aborta o POST inteiro.
     */
    private suspend fun httpForm(
        method: String,
        url: String,
        form: FormData,
        token: String
    ): String = if (isSafariOrIosWebClient()) {
        httpFormXhr(method, url, form, token)
    } else {
        httpFormFetch(method, url, form, token)
    }

    private suspend fun httpFormXhr(
        method: String,
        url: String,
        form: FormData,
        token: String
    ): String = suspendCancellableCoroutine { cont ->
        try {
            val xhr: dynamic = js("new XMLHttpRequest()")
            xhr.open(method, url, true)
            xhr.timeout = 300_000
            xhr.setRequestHeader("Accept", "application/json")
            xhr.setRequestHeader("Authorization", "Bearer $token")
            applyIcdClientHeaders(xhr)
            xhr.onload = {
                val status = (xhr.status as? Number)?.toInt() ?: 0
                val text = (xhr.responseText as? String) ?: ""
                if (status >= 400) {
                    val errMsg = try {
                        val p = JSON.parse<dynamic>(text)
                        (p.error as? String) ?: text
                    } catch (_: Throwable) {
                        text.ifBlank { "HTTP $status" }
                    }
                    resumeForm(cont, Result.failure(IllegalStateException(errMsg)))
                } else if (status == 0) {
                    resumeForm(cont, Result.failure(Exception("Failed to fetch")))
                } else {
                    resumeForm(cont, Result.success(text))
                }
            }
            xhr.onerror = {
                resumeForm(cont, Result.failure(Exception("Failed to fetch")))
            }
            xhr.ontimeout = {
                resumeForm(cont, Result.failure(Exception("timeout")))
            }
            xhr.onabort = {
                resumeForm(cont, Result.failure(Exception("abort")))
            }
            xhr.send(form)
        } catch (t: Throwable) {
            resumeForm(cont, Result.failure(friendlyNetworkError(t)))
        }
    }

    private suspend fun httpFormFetch(
        method: String,
        url: String,
        form: FormData,
        token: String
    ): String = suspendCancellableCoroutine { cont ->
        try {
            val opts = js("{}")
            opts.method = method
            opts.mode = "cors"
            opts.headers = js("{}")
            opts.headers["Accept"] = "application/json"
            opts.headers["Authorization"] = "Bearer $token"
            opts.headers["X-ICD-Platform"] = "web"
            val ver = icdClientVersion()
            if (ver.isNotBlank()) opts.headers["X-ICD-Version"] = ver
            // Sem keepalive: Safari iOS aborta multipart > ~64KB.
            opts.body = form
            val promise: dynamic = js("fetch")(url, opts)
            promise.then({ response: dynamic ->
                response.text().then({ text: dynamic ->
                    val status = response.status as Int
                    if (status >= 400) {
                        val errMsg = try {
                            val p = JSON.parse<dynamic>(text as String)
                            (p.error as? String) ?: (text as String)
                        } catch (_: Throwable) {
                            text as String
                        }
                        resumeForm(cont, Result.failure(IllegalStateException(errMsg)))
                    } else {
                        resumeForm(cont, Result.success(text as String))
                    }
                }, { err: dynamic ->
                    resumeForm(cont, Result.failure(throwableFromJsErr(err)))
                })
            }, { err: dynamic ->
                resumeForm(cont, Result.failure(throwableFromJsErr(err)))
            })
        } catch (t: Throwable) {
            resumeForm(cont, Result.failure(friendlyNetworkError(t)))
        }
    }

    private suspend fun httpBytes(method: String, url: String, token: String): ByteArray =
        suspendCancellableCoroutine { cont ->
            try {
                val opts = js("{}")
                opts.method = method
                opts.headers = js("{}")
                opts.headers["Authorization"] = "Bearer $token"
                val promise: dynamic = js("fetch")(url, opts)
                promise.then({ response: dynamic ->
                    if ((response.status as Int) >= 400) {
                        response.text().then({ text: dynamic ->
                            cont.resumeWith(
                                Result.failure(IllegalStateException(text as? String ?: "erro"))
                            )
                        }, { err: dynamic ->
                            cont.resumeWith(Result.failure(Exception("$err")))
                        })
                    } else {
                        response.arrayBuffer().then({ buf: ArrayBuffer ->
                            val u8 = Uint8Array(buf)
                            val out = ByteArray(u8.length)
                            for (i in 0 until u8.length) out[i] = u8[i]
                            cont.resume(out)
                        }, { err: dynamic ->
                            cont.resumeWith(Result.failure(Exception("$err")))
                        })
                    }
                }, { err: dynamic ->
                    cont.resumeWith(Result.failure(Exception("$err")))
                })
            } catch (t: Throwable) {
                cont.resumeWith(Result.failure(t))
            }
        }
}
